import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Bot } from "lucide-react";
import { TID } from "@/constants/site";
import LogoMark from "@/components/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* --- Lead extraction from the visitor's own messages (UI-level, no AI changes) --- */
export function extractLead(messages) {
  const userTexts = messages.filter((m) => m.role === "user").map((m) => m.text);
  const emailMatch = userTexts.join("\n").match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
  if (!emailMatch) return null;
  const email = emailMatch[0];
  let name = null;
  const nameRe = /(?:my name is|i am|i'm|this is)\s+([A-Z][\w'-]+(?:\s+[A-Z][\w'-]+)?)/i;
  for (const t of userTexts) {
    const m = t.match(nameRe);
    if (m) { name = m[1]; break; }
  }
  const need = userTexts.find((t) => /\b(need|looking for|interested in|want|help with|like to)\b/i.test(t)) || userTexts[0];
  return { name: name || "", email, need: (need || "").slice(0, 500) };
}

const STARTERS = {
  home: {
    label: "Getting started",
    chips: [
      "What does AidaNova do?",
      "What are your three core ideas?",
      "Is this for small businesses or individuals?",
    ],
  },
  why: {
    label: "Why AidaNova exists",
    chips: [
      "What gap are you trying to close?",
      "Do I need to be an AI expert?",
      "What do you believe about AI?",
    ],
  },
  build: {
    label: "What we build",
    chips: [
      "What kind of AI do you build?",
      "Experiences vs. workflows — what's the difference?",
      "Do you have anything in development?",
    ],
  },
  partner: {
    label: "Ways to work together",
    chips: [
      "How can I work with you?",
      "Ready-made or custom — which fits me?",
      "Do you offer advisory if I'm unsure about AI?",
    ],
  },
  who: {
    label: "Who we are",
    chips: [
      "What are AidaNova's values?",
      "How do you handle data & privacy?",
      "How do you approach Responsible AI?",
    ],
  },
  team: {
    label: "Who we are",
    chips: [
      "What are AidaNova's values?",
      "How do you handle data & privacy?",
      "How do you approach Responsible AI?",
    ],
  },
  connect: {
    label: "Before you reach out",
    chips: [
      "What happens after I get in touch?",
      "What can I reach out about?",
      "Will I get a sales pitch?",
    ],
  },
};

const AidaChatPanel = ({ section = "home", embedded = false, onClose }) => {
  const [messages, setMessages] = useState([
    { role: "aida", text: "Hi, I'm Aida — AidaNova's assistant. Ask me anything about AidaNova, or tell me what you need and I'll pass it straight to the team. ✦" },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const endRef = useRef(null);
  const messagesRef = useRef(messages);
  const capturedRef = useRef(new Set());
  const sessionRef = useRef(`aida-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const starter = STARTERS[section] || STARTERS.home;

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const respond = async (text) => {
    const q = text.trim();
    if (!q || typing) return;

    const history = messagesRef.current;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setTyping(true);

    try {
      const res = await fetch(`${API}/aida/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: q, history }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let started = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        if (!started) {
          started = true;
          setTyping(false);
          setMessages((m) => [...m, { role: "aida", text: acc }]);
        } else {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "aida", text: acc };
            return copy;
          });
        }
      }
      if (!started) {
        setTyping(false);
        setMessages((m) => [...m, { role: "aida", text: "Sorry — I didn't catch that. Could you try rephrasing?" }]);
      }
    } catch (e) {
      setTyping(false);
      setMessages((m) => [...m, { role: "aida", text: "Sorry — I hit a snag just now. Please try again, or reach the team via the form here in Connect." }]);
    }

    // Lead capture: structured record (name / need / email), once per email
    const lead = extractLead(messagesRef.current);
    if (lead?.email && !capturedRef.current.has(lead.email)) {
      capturedRef.current.add(lead.email);
      fetch(`${API}/aida/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, ...lead }),
      }).catch(() => {});
      setMessages((m) => [
        ...m,
        { role: "aida", text: `Thanks${lead.name ? ` ${lead.name}` : ""} — I've passed this to the team, and they'll reply to ${lead.email}. Anything else I can help with?` },
      ]);
    }
  };

  return (
    <div
      className={
        embedded
          ? "flex h-full min-h-[480px] flex-col overflow-hidden rounded-3xl border border-black/10 bg-[#FFFFFF]"
          : "flex max-h-[80vh] flex-col overflow-hidden rounded-3xl border border-black/10 bg-[#FFFFFF] shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      }
      data-testid={TID.aida.panel}
    >
      {/* header */}
      <div className="flex items-center justify-between border-b border-black/10 bg-[#F4FAFB] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-[#43B0CE]/15">
            <LogoMark className="h-5 w-5" />
          </div>
          <div>
            <p className="font-display text-sm font-bold text-[#0B2429]">Aida</p>
            <p className="text-[11px] text-[#43B0CE]">AidaNova's assistant · online</p>
          </div>
        </div>
        {onClose && (
          <button data-testid={TID.aida.close} onClick={onClose} className="p-1 text-[#64787E] transition-colors hover:text-[#0B2429]">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* messages */}
      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "aida" && (
              <div className="mr-2 mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#43B0CE]/15">
                <Bot className="h-3.5 w-3.5 text-[#43B0CE]" />
              </div>
            )}
            <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed ${m.role === "user" ? "bg-[#43B0CE] text-white" : "border border-black/10 bg-[#EDF8F8] text-[#3C5156]"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {typing && (
          <div className="flex items-center gap-1.5 pl-8">
            {[0, 1, 2].map((d) => (
              <motion.span key={d} className="h-2 w-2 rounded-full bg-[#64787E]"
                animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }} />
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* section-aware prompt starters */}
      <div className="px-5 pb-2">
        <AnimatePresence mode="wait">
          <motion.div
            key={section}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.3 }}
          >
            <p className="mb-2 text-[11px] text-[#64787E]">Suggested · {starter.label}</p>
            <div className="flex flex-wrap gap-2">
              {starter.chips.map((c, i) => (
                <button
                  key={c}
                  data-testid={TID.aida.chip(i)}
                  onClick={() => respond(c)}
                  disabled={typing}
                  className="rounded-full border border-black/10 bg-[#EDF8F8] px-3 py-1.5 text-[11px] text-[#3C5156] transition-all hover:border-[#43B0CE]/40 hover:text-[#43B0CE] disabled:opacity-50"
                >
                  {c}
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* input */}
      <form
        onSubmit={(e) => { e.preventDefault(); respond(input); }}
        className="flex items-center gap-2 border-t border-black/10 px-5 py-4"
      >
        <input
          data-testid={TID.aida.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask Aida anything…"
          className="flex-1 rounded-full border border-black/10 bg-[#EDF8F8] px-4 py-2.5 text-[13px] text-[#0B2429] placeholder:text-[#94A7AC] focus:border-[#43B0CE]/50 focus:outline-none"
        />
        <button data-testid={TID.aida.send} type="submit" className="grid h-10 w-10 place-items-center rounded-full bg-[#43B0CE] text-white transition-colors hover:bg-[#5FC4DB]">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
};

const AidaWidget = ({ open, onClose, section = "home", embedded = false }) => {
  if (embedded) return <AidaChatPanel section={section} embedded onClose={undefined} />;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[70] bg-[#0B2429]/25 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-4 left-4 right-4 z-[71] sm:left-auto sm:w-[420px]"
          >
            <AidaChatPanel section={section} onClose={onClose} />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AidaWidget;
